import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { formatEpochDate } from 'utilities/dateHelpers';
import useCompanies from 'hooks/useCompanies';
import getInvoiceCostVisibility from 'screens/Invoices/helpers/getInvoiceCostVisibility';
import DetailPanel from 'components/RecordsListView/DetailPanel';
import { mapToStandardColor } from 'constants/colors';
import Tag from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection/components/Tag';
import { useSummaryParams } from 'components/RecordsListView/context/SummaryParamsContext';
import { OperationType } from 'components/RecordsListView/constants';
import InvoiceActionMenu from './InvoiceActionMenu';
import InvoiceProductsTab from './InvoiceProductsTab';
import useInvoiceDetails from '../hooks/useInvoiceDetails';
import { InvoiceSummary } from '../types';
// Card-tab styling shared with the Applications/Work Orders detail view.
import 'screens/Applications&WorkOrders/Components/DetailsTabs/index.css';
import 'screens/Applications&WorkOrders/Components/DetailsTabs/components/ProductsTab/index.css';
import 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/DetailsTab.css';
import './InvoiceDetailPanel.css';

const NOT_AVAILABLE = '---';

export interface InvoiceDetailPanelProps {
  /** Selected invoice id; `null` closes the panel (empty state). */
  operationId: string | null;
  /** Selected summary row — supplies the header title + tag chips instantly. */
  invoiceSummary?: InvoiceSummary;
  /** Whether the list has any invoices (drives the search/filter empty states). */
  hasInvoices: boolean;
  /** Summary-level loading state from the list. */
  isLoading?: boolean;
  /** Member role used to mask cost fields. */
  memberRole?: string;
  /** Member financial access used to mask cost fields. */
  memberFinancialAccess?: string;
  /** Whether the member can perform write actions (gates the action menu). */
  canModify?: boolean;
  /** Fired after a successful delete so the list can re-select + refetch. */
  onInvoiceDeleted?: (invoiceId: string) => void;
  /** Fired on Escape / explicit dismiss so the parent can close the panel. */
  onClose?: () => void;
}

/**
 * One label/value row inside the right-hand overview `detail-card`, matching the
 * Applications `DetailOverview` rows (fixed-width neutral label + value).
 */
const InfoRow: React.FC<{
  label: string;
  children: React.ReactNode;
  noWrap?: boolean;
  /**
   * Compact label width (Figma "Work Order Key Inputs V2" widget, 81px) for
   * the overview card row, instead of the fixed `invoice-details-field-label`
   * class used by the Details tab's field list.
   */
  compact?: boolean;
}> = ({ label, children, noWrap = false, compact = false }) => (
  <div
    className={`details-field-row flex items-start gap-5 py-2 border-b border-neutral-20 leading-5 min-w-0${
      compact ? ' overview-field-row' : ''
    }`}
  >
    <div
      className={`text-sm text-neutral-60 shrink-0${
        compact ? ' overview-field-label' : ' invoice-details-field-label'
      }`}
    >
      {label}
    </div>
    <div
      className={`text-sm text-neutral-1000 min-w-0 flex-1${
        noWrap ? ' whitespace-nowrap overflow-hidden truncate' : ''
      }`}
    >
      {children}
    </div>
  </div>
);

/**
 * Inline detail side panel for a selected invoice (US2). Wraps the shared
 * `<DetailPanel operationType={OperationType.INVOICE}>` shell with an invoice
 * read-only body (mirroring the legacy full-page `InvoiceView` fields) and the
 * `InvoiceActionMenu`.
 *
 * The legacy `InvoiceDetailsTab`/`InvoiceProductsTab` are Formik-bound *edit*
 * forms (and pull in GrowUI), so they are intentionally NOT reused here — that
 * would violate the read-only intent and Constitution § III (no new GrowUI).
 * Instead the read-only fields are rendered directly with the UI-kit/Tailwind
 * primitives the redesign already uses.
 *
 * The shared `DetailPanel` does not manage open/close, so Escape handling lives
 * here and calls `onClose`; cost fields are masked via `getInvoiceCostVisibility`.
 */
const InvoiceDetailPanel: React.FC<InvoiceDetailPanelProps> = ({
  operationId,
  invoiceSummary,
  hasInvoices,
  isLoading = false,
  memberRole,
  memberFinancialAccess,
  canModify = false,
  onInvoiceDeleted,
  onClose
}) => {
  const { companies } = useCompanies() as {
    companies: Array<{ id: string; name: string }>;
  };
  const { hasSearchTerm, hasFilters } = useSummaryParams();
  const [activeTab, setActiveTab] = useState('products');
  const {
    invoiceDetails,
    isLoading: isLoadingDetails,
    errors: detailsError
  } = useInvoiceDetails(operationId);

  // Close on Escape — the shared DetailPanel does not own open/close state.
  useEffect(() => {
    if (!operationId || !onClose) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || hasSearchTerm) return;

      const isTextEntryTarget = (target: EventTarget | null) =>
        target instanceof HTMLElement &&
        target.closest('input, textarea, [contenteditable="true"]');

      if (
        isTextEntryTarget(event.target) ||
        isTextEntryTarget(document.activeElement)
      ) {
        return;
      }

      onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasSearchTerm, operationId, onClose]);

  const maskCost = (value: number | null | undefined): string =>
    getInvoiceCostVisibility({
      cost: value ?? 0,
      memberRole,
      memberFinancialAccess
    }) || '';

  const vendorName = useMemo(() => {
    const vendorId = invoiceDetails?.vendor ?? invoiceSummary?.vendor;
    if (!vendorId || !companies?.length) return NOT_AVAILABLE;
    return (
      companies.find(company => company.id === vendorId)?.name || NOT_AVAILABLE
    );
  }, [invoiceDetails, invoiceSummary, companies]);

  const products = invoiceDetails?.invoiceProducts ?? [];
  const totalCost = products.reduce(
    (sum, product) => sum + (product.totalCostValue ?? 0),
    0
  );

  const cropSeasonNames = (invoiceDetails?.cropSeasons ?? [])
    .map(season => season.name)
    .filter(Boolean);

  const title = invoiceDetails?.invoiceNumber || invoiceSummary?.title || '';
  const showDetails = Boolean(operationId);
  // useInvoiceDetails only reports loading once its own effect has started the
  // fetch, so the render right after a row is selected would otherwise read as
  // "no results" until then.
  const isWaitingForDetails =
    showDetails && invoiceDetails?.id !== operationId && !detailsError;
  const isEmpty =
    !isWaitingForDetails &&
    (!showDetails || (!isLoadingDetails && !invoiceDetails));

  const actionInvoice = {
    invoiceId: operationId ?? '',
    title,
    invoiceNumber: title,
    vendor: invoiceDetails?.vendor ?? invoiceSummary?.vendor,
    flags: invoiceSummary?.flags,
    invoiceDate: invoiceSummary?.invoiceDate,
    invoiceDateEpoch:
      invoiceDetails?.invoiceDateEpoch ?? invoiceSummary?.invoiceDateEpoch,
    invoiceDueDate: invoiceSummary?.invoiceDueDate,
    invoiceDueDateEpoch:
      invoiceDetails?.invoiceDueDateEpoch ??
      invoiceSummary?.invoiceDueDateEpoch,
    // The PDF renderer needs `{ name }` objects; the detail fetch already
    // returns that shape, the list summary only has a formatted string/array.
    cropSeasons:
      invoiceDetails?.cropSeasons ??
      (Array.isArray(invoiceSummary?.cropSeasons)
        ? invoiceSummary?.cropSeasons.map(name => ({ name }))
        : []),
    invoiceProducts: invoiceDetails?.invoiceProducts,
    // Carried into the Application/Work Order conversion. The conversion hook
    // only refetches the invoice when `invoiceProducts` is absent, so without
    // these the tags/notes it copies onto the new record are always undefined.
    flagIds: invoiceDetails?.flagIds,
    notes: invoiceDetails?.notes
  };

  return (
    <DetailPanel
      title={title}
      operationType={OperationType.INVOICE}
      isLoading={isLoading || isLoadingDetails || isWaitingForDetails}
      isEmpty={isEmpty}
      showSearchEmptyState={hasSearchTerm && !hasInvoices}
      showFilterEmptyState={hasFilters && !hasInvoices}
      testId="invoice-detail-panel"
      headerClassName="flex items-center justify-between gap-3 bg-white pb-15px p-4 border-b border-color-neutral-20"
      titleClassName="text-2xl leading-8 font-semibold text-neutral-1000 truncate flex-1 min-w-0"
      contentClassName="pt-4 pb-8 px-4 flex flex-col flex-1 min-w-0"
      actionMenu={
        showDetails && !isEmpty ? (
          <InvoiceActionMenu
            invoice={actionInvoice}
            canModify={canModify}
            onInvoiceDeleted={onInvoiceDeleted}
          />
        ) : (
          undefined
        )
      }
    >
      <div
        data-testid="invoice-detail-body"
        className="min-w-0 flex flex-col flex-1"
      >
        <div className="flex gap-4 mb-4">
          <section className="detail-card overview-total-cost-card min-w-0">
            <div className="flex items-start gap-4 h-full">
              <div className="shrink-0">
                <div className="text-sm text-neutral-60 leading-1.375rem whitespace-nowrap">
                  Total Cost
                </div>
                <div className="text-2xl font-semibold text-neutral-1000 leading-8 whitespace-nowrap">
                  {maskCost(totalCost) || NOT_AVAILABLE}
                </div>
              </div>
              <div className="overview-divider shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-neutral-60 leading-1.375rem whitespace-nowrap">
                  Vendor
                </div>
                <div className="text-2xl font-semibold text-neutral-1000 leading-8 truncate">
                  {vendorName}
                </div>
              </div>
            </div>
          </section>

          <section className="detail-card overview-date-card px-6 py-4 flex flex-col gap-2">
            <InfoRow label="Invoice Date" compact>
              {invoiceDetails?.invoiceDateEpoch &&
              invoiceDetails.invoiceDateEpoch > 0
                ? formatEpochDate(invoiceDetails.invoiceDateEpoch)
                : NOT_AVAILABLE}
            </InfoRow>
            <InfoRow label="Due Date" compact>
              {invoiceDetails?.invoiceDueDateEpoch &&
              invoiceDetails.invoiceDueDateEpoch > 0
                ? formatEpochDate(invoiceDetails.invoiceDueDateEpoch)
                : NOT_AVAILABLE}
            </InfoRow>
          </section>
        </div>

        {/* Products / Notes in card-style tabs, mirroring the Applications &
            Work Orders detail view (DetailsTabs). */}
        <div className="operation-details-tabs">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            className="leading-5"
            items={[
              {
                key: 'products',
                label: 'Products',
                forceRender: true,
                children: (
                  <InvoiceProductsTab
                    products={products}
                    memberRole={memberRole}
                    memberFinancialAccess={memberFinancialAccess}
                    isActiveTab={activeTab === 'products'}
                  />
                )
              },
              {
                key: 'details',
                label: 'Details',
                forceRender: true,
                children: (
                  <div className="px-6 py-8 flex flex-col gap-2 min-w-0">
                    {/* Company is intentionally NOT shown in the Details tab
                        (FR-098): it already appears in the overview card above
                        and remains an input in the create/edit Details step. */}
                    <InfoRow label="Crop Season(s)" noWrap>
                      {cropSeasonNames.length > 0
                        ? cropSeasonNames.join(', ')
                        : NOT_AVAILABLE}
                    </InfoRow>
                    <div className="details-field-row flex items-start gap-5 py-2 mb-40px border-b border-neutral-20 leading-5 min-w-0">
                      <div className="invoice-details-field-label text-sm text-neutral-60 shrink-0">
                        Tags
                      </div>
                      <div className="flex-1 min-w-0 flex flex-wrap gap-2">
                        {invoiceSummary?.flags &&
                        invoiceSummary.flags.length > 0 ? (
                          invoiceSummary.flags.map(flag => {
                            const colorMapping = mapToStandardColor(flag.color);
                            return (
                              <Tag
                                key={flag.id}
                                name={flag.name}
                                backgroundColor={colorMapping.background}
                                textColor={colorMapping.text}
                                fontSize="12px"
                                paddingRight="8px"
                                paddingLeft="8px"
                              />
                            );
                          })
                        ) : (
                          <span className="text-sm text-neutral-1000">
                            {NOT_AVAILABLE}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              },
              {
                key: 'notes',
                label: 'Notes',
                forceRender: true,
                children: (
                  <div
                    className="p-6 invoice-detail-notes-scroll"
                    style={{ maxHeight: 'calc(100vh - 320px)' }}
                  >
                    <div
                      className="text-sm text-neutral-1000 whitespace-pre-wrap"
                      style={{ paddingBottom: '48px' }}
                    >
                      {invoiceDetails?.notes || 'No notes entered'}
                    </div>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    </DetailPanel>
  );
};

export default InvoiceDetailPanel;
